import React, { useEffect, useRef, useState } from 'react';
import Facebook from '../../static/icons/facebook.svg';
import Twitter from '../../static/icons/twitter.svg';
import CopyLink from '../../static/icons/copy-link.svg';
import Email from '../../static/icons/email.svg';
import ShareIcon from '../../static/icons/share-bold.svg';
import { Box } from '@3pc/layout-components-react';
import { colors } from '../styles/colors';
import { isTouchDevice } from '../utils';
import styled from '@emotion/styled';
import { devices } from '../styles/breakpoints';

const ShareButtonTag = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: transparent;
  border-radius: 4px;
  transition: all 200ms ease-in-out;

  span {
    display: inline-block;
    margin-top: 8px;
    font-size: 0.625em;
    line-height: 0.688em;
  }

  @media ${devices.nonTouchable} {
    &:hover {
      box-shadow: 0 0 0 15px ${colors['blue-rgba-one']};
      background: ${colors['blue-rgba-one']};
    }
  }

  &:focus-visible {
    box-shadow: 0 0 0 15px ${colors['blue-rgba-one']};
    background: ${colors['blue-rgba-one']};
  }

  &:active {
    box-shadow: 0 0 0 15px ${colors['blue-rgba-three']};
    background: ${colors['blue-rgba-three']};
  }

  &:focus {
    outline: 0;
  }
`;

const SocialMediaLinksBox = styled.ul`
  display: none;
  position: absolute;
  left: 20px;
  opacity: 0;
  align-self: flex-start;
  transition: all 200ms ease-in;
  margin-left: 20px;
  margin-top: -5px;
  padding: 5px;
  border-radius: 5px;
  box-shadow: 1px 1px 10px 1px rgba(0, 0, 0, 0.1);

  & li {
    margin-right: 7px;
    align-self: center;
    height: 24px;
  }

  & li:first-of-type {
    margin-left: 7px;
  }

  &[aria-hidden='false'] {
    display: flex;
    opacity: 1;

    li svg {
      background: transparent;
      fill: ${colors.white};
    }

    li a,
    li button {
      background: ${colors.blue};
      border-radius: 50%;
      height: 24px;
      width: 24px;
      display: inline-flex;
      padding: 3px;
      justify-content: center;
      align-items: center;
      box-shadow: 0 0 0 0px ${colors.blue};
      transition: all 200ms ease-in-out;

      @media ${devices.nonTouchable} {
        &:hover {
          box-shadow: 0 0 0 2px ${colors.blue};
          padding: 0px;
        }
      }

      &:active {
        box-shadow: 0 0 0 2px ${colors.blue};
        padding: 0px;
      }

      &:focus-visible {
        box-shadow: 0 0 0 2px ${colors.blue}, 0 0 0 6px orange;
        padding: 0px;
      }

      &:focus {
        outline: 0;
      }
    }
  }
`;

const Toast = styled.div`
  position: relative;

  span {
    font-size: 0.625em;
    line-height: 0.938em;
    padding: 2.5px;
    top: 32px;
    left: 14px;
    display: inline-block;
    position: absolute;
    width: 145px;
    box-shadow: 1px 1px 10px 1px rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }
`;

const ShareButton = (title) => {
  const [activeShare, setActiveShare] = useState(false);
  const [timer, setTimer] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const copyButton = useRef(null);
  const [currentURL, setCurrentURL] = useState('');
  const [touchDeviceState, settouchDeviceState] = useState(false);

  const handleShare = () => {
    if (navigator.share && touchDeviceState) {
      navigator
        .share({
          title,
          url: currentURL,
        })
        .then(() => {
          console.log('Thanks for sharing!');
        })
        .catch(console.error);
    } else {
      setActiveShare(!activeShare);
    }
  };

  const copyToClipboard = () => {
    // close already open toast (just in case)
    setShowToast(false);
    timer && clearTimeout(timer);
    setTimer(null);

    // copy to clipboard
    const str = encodeURI(currentURL);

    navigator.clipboard.writeText(str).then(
      () => {
        // tell user that copying was successful
        setShowToast(true);

        // close after 5sec.
        setTimer(setTimeout(() => setShowToast(false), 5000));
      },
      () => {
        console.log('Houston, we have a problem!');
      }
    );
  };

  useEffect(() => {
    setCurrentURL(window.location.href);
    settouchDeviceState(isTouchDevice());
  }, []);

  return (
    <>
      <Box display="flex" position="relative">
        <ShareButtonTag
          id="share-btn"
          aria-expanded={activeShare}
          aria-controls="share-panel"
          onClick={handleShare}
        >
          <ShareIcon width="24px" height="24px" />
          <span>Teilen</span>
        </ShareButtonTag>
        <SocialMediaLinksBox
          id="share-panel"
          activeShare={activeShare}
          aria-hidden={!activeShare}
          aria-labelledby="share-btn"
        >
          <li>
            <a
              href={`http://www.facebook.com/sharer.php?u=${encodeURI(
                currentURL
              )}`}
              rel="noopener noreferrer"
              target="_blank"
              title="Auf Facebook teilen"
            >
              <span className="sr-only">Auf Facebook teilen</span>
              <Facebook />
            </a>
          </li>
          <li>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURI(
                currentURL
              )}`}
              rel="noopener noreferrer"
              target="_blank"
              title="Auf Twitter teilen"
            >
              <span className="sr-only">Auf Twitter teilen</span>
              <Twitter />
            </a>
          </li>
          <li>
            <a
              href={`mailto:?subject=Wissenswertes zur Hessischen Landesverfassung&body=Hier sind einige interessante Informationen zur Hessischen Landesverfassung: ${currentURL}`}
              title="mit E-Mail teilen"
            >
              <span className="sr-only">mit E-Mail teilen</span>
              <Email />
            </a>
          </li>
          <li>
            <button ref={copyButton} onClick={copyToClipboard} title="Kopieren">
              <span className="sr-only">Test URL kopieren</span>
              <CopyLink />
            </button>
          </li>
        </SocialMediaLinksBox>
        {showToast && (
          <Toast>
            <span role="status" aria-live="polite">
              URL wurde erfolgreich kopiert!
            </span>
          </Toast>
        )}
      </Box>
    </>
  );
};

export default ShareButton;
